import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import PageTabs from '@/components/shared/PageTabs';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { useFeatures } from '@/hooks/useFeatures';
import { useTable } from '@/hooks/useTable';
import { useTranslator } from '@/hooks/useTranslator';
import { fallback } from '@/lib/utils/zodFallback';
import { ProductDetailHeader } from '../../../-components/ProductDetailHeader';
import { ProductLotsTable } from '../../../-components/ProductLotsTable';
import { useProductDetail } from '../../../-hooks/useProductDetail';
import { useProductLots } from '../../../-hooks/useProductLots';

const searchSchema = z.object({
  tab: fallback(z.enum(['lots', 'archived-lots']), 'lots'),
});

export const Route = createFileRoute(
  '/(client)/inventory/(pages)/(detail)/$productId/',
)({
  validateSearch: searchSchema,
  component: ProductDetailPage,
});

function buildFilter(filters: Record<string, string[]>): string {
  const flat: Record<string, string> = {};
  for (const [key, vals] of Object.entries(filters)) {
    const v = vals[0];
    if (vals.length > 0 && v !== undefined) flat[key] = v;
  }
  return Object.keys(flat).length ? JSON.stringify(flat) : '';
}

function ProductDetailPage() {
  const { productId } = Route.useParams();
  const { tab } = Route.useSearch();
  const { getTranslation: t } = useTranslator({ page: 'inventory' });
  const { isFeatureEnabled } = useFeatures();
  const isArchived = tab === 'archived-lots';

  const lotsStorageKey = `inventory-lots-${isArchived ? 'archived' : 'active'}`;
  const table = useTable(lotsStorageKey);
  const filter = buildFilter(table.filters);

  const { data: product, isFetching: isProductFetching } =
    useProductDetail(productId);
  const { data: lotsData, isFetching: isLotsFetching } = useProductLots(
    productId,
    {
      page: table.page,
      limit: table.limit,
      ...(table.queryParams.sort ? { sort: table.queryParams.sort } : {}),
      ...(filter ? { filter } : {}),
      isArchived,
    },
  );

  if (isProductFetching) {
    return (
      <div className='flex flex-col gap-4 p-6'>
        <Skeleton className='h-8 w-64' />
        <Skeleton className='h-4 w-40' />
        <Skeleton className='h-4 w-32' />
      </div>
    );
  }

  if (!product)
    return (
      <EmptyState
        title={t('error.notFound')}
        description={t('error.notFoundDescription')}
      />
    );

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
      <ProductDetailHeader product={product} />
      <PageTabs
        tabs={[
          {
            key: 'lots',
            label: t('detail.tabs.lots'),
            to: `/inventory/${productId}`,
          },
          {
            key: 'archived-lots',
            label: t('detail.tabs.archivedLots'),
            to: `/inventory/${productId}?tab=archived-lots`,
          },
        ]}
        mode='search'
        param='tab'
        defaultTab='lots'
        moreLabel={t('tabs.more')}
      />
      <PageContainer>
        <ProductLotsTable
          data={lotsData?.data ?? []}
          isFetching={isLotsFetching}
          totalCount={lotsData?.meta?.totalCount ?? 0}
          table={table}
          storageKey={lotsStorageKey}
          isTracingEnabled={isFeatureEnabled('fiber-forward:tracing-graph')}
        />
      </PageContainer>
    </div>
  );
}
